import { Suspense, useEffect, useRef, type ReactNode } from 'react';
import { PageLoader, useSnackbars } from '@customerio/pluma-components/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { ConnectorRouteView, JourneysConnectorHandoff, useConnectorHandoffHrefs } from '@/features/integrations';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { DocumentTitle } from '@/components/page/document-title';
import { useBreadcrumbTitle } from '@/services/breadcrumbs';
import { isEmptyString } from '@/utils/string';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { toSqlDatabaseType } from '@/features/sql-sync';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import SqlSyncListPage from './index.page';
import CreateSyncPage from './new/index.page';
import SqlImportDetailPage from './import/index.page';
import {
  SQL_SYNC_IMPORT_ROUTE_PATHS,
  SQL_SYNC_LIST_ROUTE_PATHS,
  SQL_SYNC_NEW_ROUTE_PATHS,
  sqlSyncImportRoutePaths,
  sqlSyncImportTabFromPathname,
  type SqlSyncImportTab,
  useSqlSyncImportBreadcrumbs,
  useSqlSyncListBreadcrumbs,
  useSqlSyncNewBreadcrumbs,
} from './hooks';
import { getSqlImportQueryOptions, useSqlImport } from '@/services/db/sql-import/queries';
import { getSqlImportDatabaseCache } from '@/services/db/sql-import-database/cache';
import { ErrorPage } from '@/components/page/error';
import NotFoundPage from '@/pages/not-found.page';
import { isPageNotFoundError } from '@/services/router/page-error';
import { sentryCaptureException } from '@/utils/sentry';

/**
 * Both spellings of one URL for one engine (and, for the detail, one import); the paths are typed
 * route ids, so each needs its own call.
 */
function useSqlSyncHandoffHrefs(paths: { migrated: string; legacy: string }, type: string, sqlImportId?: string) {
  const router = useRouter();
  const params = (workspaceId: string) => ({ workspaceId, type, sqlImportId });
  return useConnectorHandoffHrefs({
    buildMigrated: (workspaceId) => router.buildLocation({ to: paths.migrated, params: params(workspaceId) }).href,
    buildLegacy: (workspaceId) => router.buildLocation({ to: paths.legacy, params: params(workspaceId) }).href,
  });
}

type JourneysSqlSyncListHandoffProps = {
  type: string;
};

/**
 * Whether Hydra serves this `:type` as an engine page. The route file matches any single segment,
 * but only the six engines are pages here; anything else reads as unmigrated and stays with the
 * embedded app — the same answer the ownership map gives.
 */
function useServesSqlSyncType(type: string): boolean {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SqlSyncMigration);
  return isMigrated && toSqlDatabaseType(type) != null;
}

/** Ember's singular `sql-import` route lives at `/sql-sync/<id>`: a bare import id where the engine goes. */
function isSqlImportIdSegment(segment: string): boolean {
  return /^\d+$/.test(segment);
}

/** Whether Hydra serves this `:type` segment at all: an engine page, or the singular redirect. */
function useServesSqlSyncSegment(type: string): boolean {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SqlSyncMigration);
  return isMigrated && (toSqlDatabaseType(type) != null || isSqlImportIdSegment(type));
}

/**
 * The legacy `/journeys/integrations/sql-sync/:type` URL: Ember's page while the flag is off, a
 * forward once it is on — for an engine's list, and for the bare-id redirect.
 */
export function JourneysSqlSyncListHandoff({ type }: JourneysSqlSyncListHandoffProps) {
  const isMigrated = useServesSqlSyncSegment(type);
  const { migrated } = useSqlSyncHandoffHrefs(SQL_SYNC_LIST_ROUTE_PATHS, type);

  return <JourneysConnectorHandoff isMigrated={isMigrated} migratedHref={migrated} />;
}

/**
 * The tab title, read back from the crumb chain the page registers — leaf first, as the app being
 * replaced titled it. Rendered after the guards, so a redirected visit never retitles the tab.
 */
function SqlSyncDocumentTitle() {
  const title = useBreadcrumbTitle();
  return isEmptyString(title) ? null : <DocumentTitle title={title} />;
}

type SqlSyncListBodyProps = {
  workspaceId: string;
  type: SqlDatabaseType;
};

function SqlSyncListBody({ workspaceId, type }: SqlSyncListBodyProps) {
  useSqlSyncListBreadcrumbs(workspaceId, type);

  return (
    <>
      <SqlSyncDocumentTitle />
      <RumPageRendered>
        <SqlSyncListPage workspaceId={workspaceId} type={type} />
      </RumPageRendered>
    </>
  );
}

type SqlSyncRouteFrameProps = {
  type: string;
  /** Set on the import detail so the hand-back keeps the import in the URL. */
  sqlImportId?: string;
  paths: { migrated: string; legacy: string };
  /**
   * Forward accounts for which Pipelines Reverse ETL has replaced SQL sync. Only the list does,
   * as Ember's index route did; the wizard keeps its draft.
   */
  withReverseEtlForward?: boolean;
  children: (workspaceId: string, type: SqlDatabaseType) => ReactNode;
};

/**
 * The gating every SQL sync route view shares. `ConnectorRouteView` applies the gates Ember's
 * `integrations` root route applied — the SQL sync routes nest under it — and hands back to the
 * `journeys` twin while the flag is off or the segment is not an engine Hydra serves. With
 * `withReverseEtlForward`, accounts for which Pipelines Reverse ETL has replaced SQL sync are
 * forwarded to its counterpart there, per engine.
 */
function SqlSyncRouteFrame({
  type,
  sqlImportId,
  paths,
  withReverseEtlForward = false,
  children,
}: SqlSyncRouteFrameProps) {
  const isMigrated = useServesSqlSyncType(type);
  const isReverseEtlConsolidated = useFeatureFlag(AccountFeatureFlag.ReverseEtlConsolidation);
  const { legacy } = useSqlSyncHandoffHrefs(paths, type, sqlImportId);

  return (
    <ConnectorRouteView isMigrated={isMigrated} legacyHref={legacy}>
      {(workspaceId) => {
        // `isMigrated` already narrowed the segment; a null here cannot render, so it hands back.
        const databaseType = toSqlDatabaseType(type);
        if (databaseType == null) {
          return null;
        }

        if (withReverseEtlForward && isReverseEtlConsolidated) {
          return (
            <SafeNavigate
              to={generatePath('/workspaces/:workspaceId/pipelines/sources/reverse-etl/:sourceOptionId', {
                workspaceId,
                sourceOptionId: databaseType,
              })}
              options={{ shouldReplaceHistoryEntry: true }}
            />
          );
        }

        return children(workspaceId, databaseType);
      }}
    </ConnectorRouteView>
  );
}

type SqlSyncListRouteViewProps = {
  type: string;
};

const MISSING_TYPE_MESSAGE = 'Unknown database type for this SQL import';

type SqlImportRedirectProps = {
  workspaceId: string;
  sqlImportId: string;
};

/** What the singular redirect resolved to: still loading, a reason it cannot move, or where to go. */
type RedirectTarget =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'unknownEngine' }
  | { status: 'ready'; type: SqlDatabaseType };

/**
 * Resolve the import behind a bare id to the engine its detail URL needs, and report an import
 * whose database has no known engine once per visit, as Ember's route did.
 *
 * The read's error is handled rather than thrown: the authenticated layout wraps the page in an
 * error boundary, so a thrown 404 would render its generic "this section couldn't load" panel
 * instead of the not-found page a stale bookmark deserves.
 */
function useSqlImportRedirectTarget(workspaceId: number, sqlImportId: string): RedirectTarget {
  const qc = useQueryClient();
  const snackbars = useSnackbars();
  const query = useQuery(getSqlImportQueryOptions(workspaceId, sqlImportId, qc));
  // The import and its database are read together once the fetch has settled: the import is stale
  // on arrival (`staleTime: 0`), and a cached import must not be paired with a database still
  // loading.
  const settled = query.data != null && !query.isFetching ? query.data : null;
  const database = settled == null ? undefined : getSqlImportDatabaseCache(qc, workspaceId, settled.database_id);
  const type = database == null ? null : toSqlDatabaseType(database.type);
  const isTypeMissing = settled != null && type == null;

  const reportedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isTypeMissing || reportedFor.current === sqlImportId) {
      return;
    }
    reportedFor.current = sqlImportId;
    sentryCaptureException(new Error('SQLImport model is missing a database type'), {
      extra: { import_id: sqlImportId },
    });
    snackbars.create({ id: 'sqlImports.redirect.missingType', variant: 'critical', message: MISSING_TYPE_MESSAGE });
  }, [isTypeMissing, sqlImportId, snackbars]);

  if (query.isError) {
    return { status: 'failed', error: query.error };
  }
  if (settled == null) {
    return { status: 'loading' };
  }
  return type == null ? { status: 'unknownEngine' } : { status: 'ready', type };
}

/**
 * Ember's `sql-import` route: load the import, read its database's engine, and move to the typed
 * detail URL. An import whose database has no known engine is reported, as Ember reported it, and
 * shown as an error instead of a blank page.
 */
function SqlImportRedirect({ workspaceId, sqlImportId }: SqlImportRedirectProps) {
  const router = useRouter();
  const location = useLocation();
  const target = useSqlImportRedirectTarget(Number(workspaceId), sqlImportId);

  if (target.status === 'loading') {
    return <PageLoader label="Loading SQL sync…" />;
  }
  if (target.status === 'failed') {
    return isPageNotFoundError(target.error) ? <NotFoundPage /> : <ErrorPage error={target.error} />;
  }
  if (target.status === 'unknownEngine') {
    return <ErrorPage title={MISSING_TYPE_MESSAGE} />;
  }
  // The query string and fragment travel along, as the shared handoffs keep them.
  const detail = router.buildLocation({
    to: SQL_SYNC_IMPORT_ROUTE_PATHS.migrated,
    params: { workspaceId, type: target.type, sqlImportId },
  }).href;
  const hash = isEmptyString(location.hash) ? '' : `#${location.hash}`;
  return <SafeNavigate to={`${detail}${location.searchStr}${hash}`} options={{ shouldReplaceHistoryEntry: true }} />;
}

type SqlImportRedirectRouteViewProps = {
  sqlImportId: string;
};

/**
 * `/integrations/sql-sync/<id>`, the singular redirect. Same flag and permission gates as the list;
 * the bare id sits where the engine does, so the list paths spell both of its URLs.
 */
function SqlImportRedirectRouteView({ sqlImportId }: SqlImportRedirectRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SqlSyncMigration);
  const { legacy } = useSqlSyncHandoffHrefs(SQL_SYNC_LIST_ROUTE_PATHS, sqlImportId);

  return (
    <ConnectorRouteView isMigrated={isMigrated} legacyHref={legacy}>
      {(workspaceId) => <SqlImportRedirect workspaceId={workspaceId} sqlImportId={sqlImportId} />}
    </ConnectorRouteView>
  );
}

/**
 * The migrated `/integrations/sql-sync/:type` list, or, for a bare import id in the engine's place,
 * the singular redirect.
 *
 * Flag off → this URL has no Ember counterpart (Ember only knows the `journeys` form), so rendering
 * the embedded host here would 404 inside Ember. Hand back to the `journeys` twin instead, which
 * Ember does serve — the exact inverse of `JourneysSqlSyncListHandoff`.
 */
export function SqlSyncListRouteView({ type }: SqlSyncListRouteViewProps) {
  if (isSqlImportIdSegment(type)) {
    return <SqlImportRedirectRouteView sqlImportId={type} />;
  }
  return (
    <SqlSyncRouteFrame type={type} paths={SQL_SYNC_LIST_ROUTE_PATHS} withReverseEtlForward>
      {(workspaceId, databaseType) => <SqlSyncListBody workspaceId={workspaceId} type={databaseType} />}
    </SqlSyncRouteFrame>
  );
}

type JourneysSqlSyncNewHandoffProps = {
  type: string;
};

/** The legacy `/journeys/integrations/sql-sync/:type/new` URL, with its `?id` draft param carried across. */
export function JourneysSqlSyncNewHandoff({ type }: JourneysSqlSyncNewHandoffProps) {
  const isMigrated = useServesSqlSyncType(type);
  const { migrated } = useSqlSyncHandoffHrefs(SQL_SYNC_NEW_ROUTE_PATHS, type);

  return <JourneysConnectorHandoff isMigrated={isMigrated} migratedHref={migrated} />;
}

type SqlSyncNewBodyProps = {
  workspaceId: string;
  type: SqlDatabaseType;
  draftId: string | null;
};

function SqlSyncNewBody({ workspaceId, type, draftId }: SqlSyncNewBodyProps) {
  useSqlSyncNewBreadcrumbs(workspaceId, type);
  const navigate = useNavigate();

  return (
    <>
      <SqlSyncDocumentTitle />
      <RumPageRendered>
        <CreateSyncPage
          workspaceId={workspaceId}
          type={type}
          draftId={draftId}
          onDraftSaved={(id) =>
            // The URL addresses the saved draft, so a refresh or a reopen resumes it instead of
            // starting a fresh sync.
            void navigate({
              to: SQL_SYNC_NEW_ROUTE_PATHS.migrated,
              params: { workspaceId, type },
              search: (current) => ({ ...current, id: Number(id) }),
              replace: true,
            })
          }
        />
      </RumPageRendered>
    </>
  );
}

type SqlSyncNewRouteViewProps = {
  type: string;
  draftId: string | null;
};

/** The migrated `/integrations/sql-sync/:type/new` create wizard; same gates as the list. */
export function SqlSyncNewRouteView({ type, draftId }: SqlSyncNewRouteViewProps) {
  return (
    <SqlSyncRouteFrame type={type} paths={SQL_SYNC_NEW_ROUTE_PATHS}>
      {(workspaceId, databaseType) => (
        <SqlSyncNewBody workspaceId={workspaceId} type={databaseType} draftId={draftId} />
      )}
    </SqlSyncRouteFrame>
  );
}

type JourneysSqlSyncImportHandoffProps = {
  type: string;
  sqlImportId: string;
  /** One of the detail's tabs, or null for the detail itself. */
  tab?: SqlSyncImportTab | null;
};

/**
 * The legacy `/journeys/integrations/sql-sync/:type/:id` URL and its tabs: Ember's pages while the
 * flag is off, a forward to the same tab once it is on.
 */
export function JourneysSqlSyncImportHandoff({ type, sqlImportId, tab = null }: JourneysSqlSyncImportHandoffProps) {
  const isMigrated = useServesSqlSyncType(type);
  const { migrated } = useSqlSyncHandoffHrefs(sqlSyncImportRoutePaths(tab), type, sqlImportId);

  return <JourneysConnectorHandoff isMigrated={isMigrated} migratedHref={migrated} />;
}

type SqlImportDetailBodyProps = {
  workspaceId: string;
  type: SqlDatabaseType;
  sqlImportId: string;
  children: ReactNode;
};

/** Registers the crumb chain with the import's name, then renders the detail around the active tab. */
function SqlImportDetailBody({ workspaceId, type, sqlImportId, children }: SqlImportDetailBodyProps) {
  const { data: sqlImport } = useSqlImport({ id: sqlImportId, workspaceId: Number(workspaceId) });
  useSqlSyncImportBreadcrumbs(workspaceId, type, sqlImportId, sqlImport.name);

  return (
    <>
      <SqlSyncDocumentTitle />
      <RumPageRendered>
        <SqlImportDetailPage workspaceId={workspaceId} type={type} sqlImportId={sqlImportId}>
          {children}
        </SqlImportDetailPage>
      </RumPageRendered>
    </>
  );
}

type SqlSyncImportRouteViewProps = {
  type: string;
  sqlImportId: string;
  /** The active tab's route, rendered inside the detail's chrome. */
  children: ReactNode;
};

/**
 * The migrated `/integrations/sql-sync/:type/:id` import detail and its tabs; same gates as the
 * list. The hand-back keeps the tab, so a flag-off visit lands on the same page in the embedded app.
 */
export function SqlSyncImportRouteView({ type, sqlImportId, children }: SqlSyncImportRouteViewProps) {
  const { pathname } = useLocation();
  const paths = sqlSyncImportRoutePaths(sqlSyncImportTabFromPathname(pathname));

  return (
    <SqlSyncRouteFrame type={type} sqlImportId={sqlImportId} paths={paths}>
      {(workspaceId, databaseType) => (
        <Suspense fallback={<PageLoader label="Loading SQL sync…" />}>
          <SqlImportDetailBody workspaceId={workspaceId} type={databaseType} sqlImportId={sqlImportId}>
            {children}
          </SqlImportDetailBody>
        </Suspense>
      )}
    </SqlSyncRouteFrame>
  );
}
